import { useState, useEffect } from 'react';

import { announcementApi, AnnouncementConfig } from '@/lib/api/announcement';

let announcementCache: AnnouncementConfig | null = null;
let fetchPromise: Promise<AnnouncementConfig> | null = null;
let lastFetchTime = 0;

const REFETCH_INTERVAL = 60_000;

const defaultConfig: AnnouncementConfig = {
  enabled: false,
};

export function useAnnouncement(): {
  config: AnnouncementConfig;
  loading: boolean;
  isActive: boolean;
} {
  const [config, setConfig] = useState<AnnouncementConfig>(
    announcementCache || defaultConfig,
  );
  const [loading, setLoading] = useState(!announcementCache);

  useEffect(() => {
    const fetchConfig = (): void => {
      const now = Date.now();

      if (announcementCache && now - lastFetchTime < REFETCH_INTERVAL) {
        setConfig(announcementCache);
        setLoading(false);
        return;
      }

      if (fetchPromise) {
        fetchPromise
          .then((data) => {
            setConfig(data);
            setLoading(false);
          })
          .catch(() => {
            setConfig(announcementCache || defaultConfig);
            setLoading(false);
          });
        return;
      }

      fetchPromise = announcementApi.getAnnouncement();
      fetchPromise
        .then((data) => {
          announcementCache = data;
          lastFetchTime = Date.now();
          setConfig(data);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Failed to fetch announcement config:', error);
          setConfig(announcementCache || defaultConfig);
          setLoading(false);
        })
        .finally(() => {
          fetchPromise = null;
        });
    };

    fetchConfig();

    const interval = setInterval(fetchConfig, REFETCH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  return { config, loading, isActive: config.enabled };
}
